import { useId, type ReactNode } from "react";

import { Alert } from "./icons";

export interface FieldProps {
  label: string;
  /** Server-authoritative diagnostic, rendered next to its own field. */
  error?: string;
  /** Hint or unit of measurement. */
  hint?: string;
  required?: boolean;
  requiredLabel?: string;
  children: (props: {
    id: string;
    "aria-describedby": string | undefined;
    "aria-invalid": boolean | undefined;
  }) => ReactNode;
}

/**
 * Label + control + diagnostics. Errors sit beside the field they belong to,
 * never collected into a summary at the top of the form.
 */
export function Field({
  label,
  error,
  hint,
  required = false,
  requiredLabel = "required",
  children,
}: FieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy =
    [error ? errorId : undefined, hint ? hintId : undefined]
      .filter(Boolean)
      .join(" ") || undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-footnote font-medium text-fg-muted">
        {label}
        {required && <span className="text-fg-subtle"> · {requiredLabel}</span>}
      </label>
      {children({
        id,
        "aria-describedby": describedBy,
        "aria-invalid": error ? true : undefined,
      })}
      {hint && (
        <p id={hintId} className="text-footnote text-fg-subtle">
          {hint}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          role="alert"
          className="flex items-center gap-1 text-footnote text-error"
        >
          <Alert size={16} />
          {error}
        </p>
      )}
    </div>
  );
}
